import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function EmailCodeDialog({
  open,
  title,
  subtitle,
  email,
  code,
  emailEditable = false,
  requestLoading = false,
  verifyLoading = false,
  error = "",
  requestLabel,
  resendInLabel = "Send again in",
  resendNowLabel = "Send again",
  secondsLabel = "seconds",
  verifyLabel,
  closeLabel = "Close",
  codePlaceholder,
  onClose,
  onEmailChange,
  onCodeChange,
  onRequestCode,
  onVerify,
}) {
  const [resendLeft, setResendLeft] = useState(60);

  const normalizedCode = String(code || "").replace(/\D+/g, "").slice(0, 6);
  const maskedEmail = useMemo(() => {
    const raw = String(email || "").trim();
    if (!raw.includes("@")) return raw;
    const [name, domain] = raw.split("@", 2);
    if (name.length <= 2) return `${name[0] || ""}***@${domain}`;
    return `${name.slice(0, 2)}***${name.slice(-1)}@${domain}`;
  }, [email]);

  useEffect(() => {
    if (!open) return;
    setResendLeft(60);
  }, [open, email]);

  useEffect(() => {
    if (!open || resendLeft <= 0) return;
    const id = window.setInterval(() => {
      setResendLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [open, resendLeft]);

  if (!open) return null;

  return (
    <div className="lp-scrim--fixed z-[80] flex items-center justify-center p-4">
      <div className="lp-veil lp-veil--edged w-full max-w-md rounded-[22px] px-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-[24px] font-bold leading-[1.1] tracking-[-0.03em] text-foreground">{title}</h3>
          <button
            type="button"
            className="lp-press lp-tile w-9 h-9 shrink-0 !rounded-full text-muted-foreground flex items-center justify-center text-2xl leading-none"
            onClick={onClose}
            aria-label={closeLabel}
          >
            ×
          </button>
        </div>
        {subtitle ? (
          <p className="mt-2 text-[14px] text-muted-foreground">
            {emailEditable ? subtitle : `${subtitle} ${maskedEmail}`}
          </p>
        ) : null}

        <form
          className="mt-5"
          onSubmit={(e) => {
            e.preventDefault();
            onVerify?.();
          }}
        >
          {emailEditable ? (
            <Input
              type="email"
              value={email}
              readOnly={!emailEditable}
              onChange={(e) => onEmailChange?.(e.target.value)}
              required
              className="h-11"
            />
          ) : null}

          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={normalizedCode}
            onChange={(e) => onCodeChange?.(String(e.target.value || "").replace(/\D+/g, "").slice(0, 6))}
            required
            placeholder={codePlaceholder}
            className="sr-only"
            aria-label={codePlaceholder}
          />

          <button
            type="button"
            className="w-full"
            onClick={() => {
              const el = document.querySelector(`input[aria-label="${codePlaceholder}"]`);
              if (el) el.focus();
            }}
          >
            <div className="grid grid-cols-6 gap-2.5">
              {Array.from({ length: 6 }).map((_, idx) => {
                const ch = normalizedCode[idx] || "";
                const active = idx === normalizedCode.length;
                return (
                  <div
                    key={idx}
                    className={`h-12 rounded-xl border flex items-center justify-center text-lg font-semibold ${active ? "border-white/65 bg-white/10" : "border-transparent bg-white/8"
                    }`}
                  >
                    <span className="text-white/90">{ch || "·"}</span>
                  </div>
                );
              })}
            </div>
          </button>

          <div className="mt-4 text-center text-white/65 text-[14px]">
            {resendLeft > 0 ? (
              <span>
                {resendInLabel} <span className="text-white font-semibold">{resendLeft} {secondsLabel}</span>
              </span>
            ) : (
              <button
                type="button"
                className="text-white font-semibold"
                disabled={requestLoading || verifyLoading}
                onClick={() => {
                  onRequestCode?.();
                  setResendLeft(60);
                }}
              >
                {resendNowLabel}
              </button>
            )}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              className="lp-tile h-11 !rounded-[14px] text-foreground"
              onClick={onClose}
            >
              {closeLabel}
            </Button>
            <Button
              type="submit"
              className="h-11 rounded-[14px]"
              disabled={verifyLoading || requestLoading || normalizedCode.length !== 6}
            >
              {verifyLabel}
            </Button>
          </div>
        </form>

        {error ? <p className="mt-3 text-sm text-destructive-fg">{error}</p> : null}
      </div>
    </div>
  );
}
